import { memo, useState, useEffect } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Clock, Trash2, MessageSquare, PlayCircle } from 'lucide-react';
import { Button, Input } from '@caruso/ui';
import { flowsService } from '@/services/flows.service';

export const ScheduleActionNode = memo(({ id, data }: NodeProps) => {
  const { setNodes, getNode } = useReactFlow();
  const [delayMinutes, setDelayMinutes] = useState(data.delayMinutes || 60);
  const [actionType, setActionType] = useState<'message' | 'flow'>(data.actionType || 'message');
  const [message, setMessage] = useState(data.message || '');
  const [flowId, setFlowId] = useState(data.flowId || '');
  const [flows, setFlows] = useState<any[]>([]);
  const [loadingFlows, setLoadingFlows] = useState(false);

  // Carregar lista de fluxos quando o tipo de ação for 'flow'
  useEffect(() => {
    if (actionType === 'flow') {
      loadFlows();
    }
  }, [actionType]);

  const loadFlows = async () => {
    setLoadingFlows(true);
    try {
      const data = await flowsService.getAll();
      setFlows(data || []);
    } catch (error) {
      console.error('Erro ao carregar fluxos:', error);
    } finally {
      setLoadingFlows(false);
    }
  };

  const handleUpdate = (updates: any) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: { ...node.data, ...updates },
          };
        }
        return node;
      })
    );
  };

  const handleDelete = () => {
    setNodes((nds) => nds.filter((node) => node.id !== id));
  };

  const handleDelayChange = (value: number) => {
    setDelayMinutes(value);
    handleUpdate({ delayMinutes: value });
  };

  const handleActionTypeChange = (type: 'message' | 'flow') => {
    setActionType(type);
    handleUpdate({ actionType: type });
  };

  const handleMessageChange = (value: string) => {
    setMessage(value);
    handleUpdate({ message: value });
  };

  const handleFlowIdChange = (value: string) => {
    setFlowId(value);
    handleUpdate({ flowId: value });
  };

  const formatTime = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  return (
    <div className="bg-neutral-900 border-2 border-purple-500/40 rounded-lg p-4 min-w-[320px] max-w-[400px]">
      <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-purple-500" />

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-purple-400" />
          <span className="font-bold text-white">Agendar Ação</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleDelete}
          className="h-6 w-6 hover:bg-red-500/20 hover:text-red-400"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-3">
        {/* Delay Input */}
        <div>
          <label className="block text-xs font-medium text-brand-ivory/70 mb-1">
            Aguardar quantos minutos?
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="1"
              max="10080"
              value={delayMinutes}
              onChange={(e) => handleDelayChange(parseInt(e.target.value) || 1)}
              className="bg-neutral-800 border-neutral-700 text-white"
            />
            <span className="text-xs text-brand-ivory/60 whitespace-nowrap">
              {formatTime(delayMinutes)}
            </span>
          </div>
          <p className="text-xs text-brand-ivory/50 mt-1">
            Máximo: 7 dias (10.080 minutos)
          </p>
        </div>

        {/* Action Type Selection */}
        <div>
          <label className="block text-xs font-medium text-brand-ivory/70 mb-2">
            Ação a executar:
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => handleActionTypeChange('message')}
              className={`p-2 rounded-lg border transition-colors flex items-center justify-center gap-2 ${
                actionType === 'message'
                  ? 'border-purple-500 bg-purple-500/20 text-purple-300'
                  : 'border-neutral-700 bg-neutral-800 text-brand-ivory/60 hover:bg-neutral-700'
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              <span className="text-xs font-medium">Mensagem</span>
            </button>
            <button
              onClick={() => handleActionTypeChange('flow')}
              className={`p-2 rounded-lg border transition-colors flex items-center justify-center gap-2 ${
                actionType === 'flow'
                  ? 'border-purple-500 bg-purple-500/20 text-purple-300'
                  : 'border-neutral-700 bg-neutral-800 text-brand-ivory/60 hover:bg-neutral-700'
              }`}
            >
              <PlayCircle className="w-4 h-4" />
              <span className="text-xs font-medium">Fluxo</span>
            </button>
          </div>
        </div>

        {/* Message Input (if message type) */}
        {actionType === 'message' && (
          <div>
            <label className="block text-xs font-medium text-brand-ivory/70 mb-1">
              Mensagem a enviar:
            </label>
            <textarea
              value={message}
              onChange={(e) => handleMessageChange(e.target.value)}
              placeholder="Digite a mensagem que será enviada após o tempo especificado..."
              className="w-full p-2 bg-neutral-800 border border-neutral-700 text-white placeholder:text-neutral-500 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50 text-sm"
              rows={4}
            />
            <p className="text-xs text-brand-ivory/50 mt-1">
              Você pode usar variáveis como {'{{nome_cliente}}'}, {'{{telefone}}'}, etc.
            </p>
          </div>
        )}

        {/* Flow Selector (if flow type) */}
        {actionType === 'flow' && (
          <div>
            <label className="block text-xs font-medium text-brand-ivory/70 mb-1">
              Selecione o Fluxo a iniciar:
            </label>
            <select
              value={flowId}
              onChange={(e) => handleFlowIdChange(e.target.value)}
              className="w-full p-2 bg-neutral-800 border border-neutral-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/50 text-sm"
            >
              <option value="">{loadingFlows ? "Carregando..." : "Selecione um fluxo"}</option>
              {flows.map((flow) => (
                <option key={flow.id} value={flow.id}>
                  {flow.name}
                </option>
              ))}
              {flows.length === 0 && !loadingFlows && (
                <option value="" disabled>
                  Nenhum fluxo disponível
                </option>
              )}
            </select>
            <p className="text-xs text-brand-ivory/50 mt-1">
              O fluxo será iniciado para o mesmo cliente após o tempo especificado
            </p>
          </div>
        )}

        {/* Summary */}
        <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-2">
          <p className="text-xs text-purple-300">
            {actionType === 'message' ? (
              <>
                <strong>Resumo:</strong> Após {formatTime(delayMinutes)}, enviar mensagem
                {message && `: "${message.substring(0, 50)}${message.length > 50 ? '...' : ''}"`}
              </>
            ) : (
              <>
                <strong>Resumo:</strong> Após {formatTime(delayMinutes)}, iniciar fluxo
                {flowId && (
                  <>
                    {' "'}
                    {flows.find(f => f.id === flowId)?.name || flowId.substring(0, 20)}
                    {'"'}
                  </>
                )}
              </>
            )}
          </p>
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-purple-500" />
    </div>
  );
});

ScheduleActionNode.displayName = 'ScheduleActionNode';
